import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Box,
  Flex,
  Grid,
  GridItem,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
} from '@chakra-ui/react'
import { Check, Play, Plus, RefreshCw, Save, Workflow, X } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { childTicketService, type TaskTemplate } from '@/lib/services/childTicketService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import TaskPipelinePreview from './TaskPipelinePreview'

type Props = {
  isOpen: boolean
  onClose: () => void
  parentTicketId: string
  existingTaskCount: number
  onApplied: () => void | Promise<void>
}

export default function ApplyTaskTemplateDrawer({
  isOpen,
  onClose,
  parentTicketId,
  existingTaskCount,
  onApplied,
}: Props) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const [templates, setTemplates] = useState<TaskTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [applying, setApplying] = useState(false)
  const { isOpen: replaceConfirmOpen, onOpen: openReplaceConfirm, onClose: closeReplaceConfirm } = useDisclosure()
  const cancelReplaceRef = useRef<HTMLButtonElement>(null)

  const hasExistingTasks = existingTaskCount > 0

  const loadTemplates = useCallback(async () => {
    setLoading(true)
    try {
      const list = await childTicketService.listTemplates()
      setTemplates(list)
    } catch (err) {
      showError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [showError])

  useEffect(() => {
    if (!isOpen) return
    setSelectedId(null)
    void loadTemplates()
  }, [isOpen, loadTemplates])

  const handleClose = () => {
    setSelectedId(null)
    onClose()
  }

  const selected = templates.find((t) => t.id === selectedId) ?? null

  const handleApply = async () => {
    if (!selectedId || hasExistingTasks) return
    setApplying(true)
    try {
      await childTicketService.applyTemplate(parentTicketId, selectedId)
      showSuccess('Task template applied')
      await onApplied()
      handleClose()
    } catch (err) {
      const message = (err as Error).message ?? ''
      showError(err as Error)
      if (/already exist|remove existing tasks/i.test(message)) {
        await onApplied()
      }
    } finally {
      setApplying(false)
    }
  }

  const handleReplace = async () => {
    if (!selectedId) return
    closeReplaceConfirm()
    setApplying(true)
    try {
      await childTicketService.replaceTemplate(parentTicketId, selectedId)
      showSuccess('Task template replaced')
      await onApplied()
      handleClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setApplying(false)
    }
  }

  return (
    <>
      <Drawer isOpen={isOpen} onClose={handleClose} placement="right" size="xl">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Apply workflow template</DrawerHeader>
          <DrawerBody py={4}>
            {hasExistingTasks ? (
              <Alert status="warning" mb={4} borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  This ticket already has {existingTaskCount} task{existingTaskCount === 1 ? '' : 's'}. Reset the
                  workflow from the Tasks tab, or use <strong>Replace with selected template</strong> below.
                </AlertDescription>
              </Alert>
            ) : null}

            {loading ? (
              <Flex py={12} justify="center">
                <Spinner />
              </Flex>
            ) : templates.length === 0 ? (
              <Text color={colors.textSecondary}>No active templates found.</Text>
            ) : (
              <Grid templateColumns={{ base: '1fr', md: '2fr 3fr' }} gap={6}>
                <GridItem>
                  <Text fontSize="xs" fontWeight="700" color={colors.textMuted} mb={2} textTransform="uppercase">
                    Templates
                  </Text>
                  <Text fontSize="xs" color={colors.textSecondary} mb={3}>
                    Select a template to preview, then confirm below.
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {templates.map((template) => {
                      const active = template.id === selectedId
                      return (
                        <Box
                          key={template.id}
                          as="button"
                          type="button"
                          textAlign="left"
                          p={3}
                          borderRadius="md"
                          border="2px solid"
                          borderColor={active ? 'blue.400' : colors.borderLight}
                          bg={active ? 'blue.50' : colors.cardBg}
                          onClick={() => setSelectedId(template.id)}
                          w="100%"
                        >
                          <Text fontSize="sm" fontWeight="700" color={colors.text}>
                            {template.name}
                          </Text>
                          <Text fontSize="xs" color={colors.textSecondary} mt={1}>
                            {template.steps?.length ?? 0} steps · {template.is_sequential ? 'Sequential' : 'Parallel'}
                          </Text>
                        </Box>
                      )
                    })}
                  </VStack>
                </GridItem>
                <GridItem>
                  <Text fontSize="xs" fontWeight="700" color={colors.textMuted} mb={2} textTransform="uppercase">
                    Preview
                  </Text>
                  {selected ? (
                    <Box>
                      <Text fontSize="md" fontWeight="700" mb={1}>
                        {selected.name}
                      </Text>
                      {selected.description ? (
                        <Text fontSize="sm" color={colors.textSecondary} mb={4}>
                          {selected.description}
                        </Text>
                      ) : null}
                      <TaskPipelinePreview
                        previewSteps={(selected.steps ?? []).map((step) => ({
                          id: step.id,
                          title: step.title,
                          status_code: step.status_code,
                          status_label: step.status_label,
                          default_sla_minutes: step.default_sla_minutes,
                          sort_order: step.sort_order,
                          form_fields: step.form_fields,
                        }))}
                        compact
                      />
                    </Box>
                  ) : (
                    <Text color={colors.textSecondary}>Select a template</Text>
                  )}
                </GridItem>
              </Grid>
            )}
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px" flexWrap="wrap" gap={2}>
            <Button size="sm" variant="outline" leftIcon={<X size={14} />} onClick={handleClose}>
              Cancel
            </Button>
            {hasExistingTasks ? (
              <Button
                size="sm"
                colorScheme="orange"
                leftIcon={<RefreshCw size={14} />}
                onClick={openReplaceConfirm}
                isLoading={applying}
                isDisabled={!selectedId || templates.length === 0}
              >
                Replace with selected template
              </Button>
            ) : (
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<Workflow size={14} />}
                onClick={() => void handleApply()}
                isLoading={applying}
                isDisabled={!selectedId || templates.length === 0}
              >
                Apply {selected?.steps?.length ?? 0} tasks
              </Button>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AlertDialog isOpen={replaceConfirmOpen} onClose={closeReplaceConfirm} leastDestructiveRef={cancelReplaceRef}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md">Replace workflow?</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              This will remove all {existingTaskCount} existing task{existingTaskCount === 1 ? '' : 's'} and apply{' '}
              <strong>{selected?.name ?? 'the selected template'}</strong>. Task progress will be lost.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelReplaceRef} size="sm" variant="outline" leftIcon={<X size={14} />} onClick={closeReplaceConfirm}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="orange" ml={3} leftIcon={<RefreshCw size={14} />} onClick={() => void handleReplace()} isLoading={applying}>
                Replace workflow
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}
